import { identity, pickBy, shuffle } from "lodash";
import type { StudentAnswerItem, StudentTestAnswer, StudentTestContent, StudentTestExercise, VerbtrainingAnswerItem } from "p6m-p6u";
import { ConnectWordsColors } from "../constants/ConnectColors";

const _initTextOnlyAnswer = (td: StudentTestExercise) => {
    const answer: Record<string, string | undefined> = {
        textAnswer: "",
        exerciseType: td.questionMode,
    };

    if (td.textFieldStarterText) {
        answer.textFieldStarterText = td.textFieldStarterText;
    }

    return answer;
};

const _initVerbTrainingAnswers = (td: StudentTestExercise): StudentAnswerItem[] => {
    const studentAnswers: StudentAnswerItem[] = [];

    td.selectedWords!.forEach((sw) => {
        const { id, name } = sw;
        const sorted = [...sw.requestedConjugations].sort((a: VerbtrainingAnswerItem, b: VerbtrainingAnswerItem) =>
            !!a.id && b.id && a.id < b.id ? -1 : 1
        );

        if (td.gapMode && td.gapMode === "columns") {
            sorted.sort((a: VerbtrainingAnswerItem, b: VerbtrainingAnswerItem) =>
                !!a.answer && b.answer && a.answer !== "" && b.answer === "" ? -1 : 1
            );
        }

        const requestedConjugations = sorted.map((c: VerbtrainingAnswerItem) => {
            return { studentAnswer: "", ...c };
        });

        studentAnswers.push({
            ...pickBy({ id, verbtrainingAnswers: requestedConjugations, verbName: name }, identity),
        });
    });

    return studentAnswers;
};

const _buildStudentAnswersForWords = (selectedWords: any[]): StudentAnswerItem[] => {
    const studentAnswers: StudentAnswerItem[] = [];

    selectedWords.forEach((sw) => {
        const { question, id, answer, gap_sentence, requestedConjugations, name } = sw;
        studentAnswers.push({
            ...pickBy({ question, id, answer, gap_sentence, requestedConjugations, name }, identity),
            studentAnswer: "",
        });
    });

    return studentAnswers;
};

const _initFillInTheGapAnswer = (td: StudentTestExercise, studentAnswers: StudentAnswerItem[]) => {
    const fillInTheGapWords = shuffle(td.fillWords || []);

    return {
        fillInTheGapWords,
        studentAnswers,
        exerciseType: td.questionMode,
    };
};

const _initJumbledWordsAnswer = (td: StudentTestExercise) => {
    const jumbledWords: string[] = shuffle(td.fillWords || []);
    const answer: Record<string, string | string[] | undefined> = {
        jumbledWordsAnswer: "",
        jumbledWords,
        exerciseType: td.questionMode,
    };

    if (td.textFieldStarterText) {
        answer.textFieldStarterText = td.textFieldStarterText;
    }

    return answer;
};

const _initVerbindenAnswer = (td: StudentTestExercise, studentAnswers: StudentAnswerItem[]) => {
    const connectWordsAnswers = shuffle(td.fillWords || []);
    const reducedStudentAnswers = studentAnswers.map(({ answer, ...rest }: StudentAnswerItem, i: number) => ({
        ...rest,
        color: ConnectWordsColors[i % ConnectWordsColors.length].name,
    }));

    return {
        studentAnswers: reducedStudentAnswers,
        connectWordsAnswers,
        exerciseType: td.questionMode,
    };
};

interface BuildStudentTestAnswerResult {
    studentTestAnswer: StudentTestAnswer;
    testHasOnlyOneExercise: boolean;
}

const buildStudentTestAnswer = (
    teacherTest: StudentTestContent,
    isPreviewOrTestRun: boolean
): BuildStudentTestAnswerResult => {
    const newStudentData: StudentTestAnswer = {
        questionAnswers: {},
        studentName: isPreviewOrTestRun ? teacherTest.teacherName : "",
    };

    if (!teacherTest.content) {
        return { studentTestAnswer: newStudentData, testHasOnlyOneExercise: false };
    }

    teacherTest.content.forEach((td) => {
        if (!td.id) return;

        const exerciseType = td.questionMode || "";

        if (td.questionMode === "textOnly") {
            newStudentData.questionAnswers![td.id] = _initTextOnlyAnswer(td);
        }

        if (!td.selectedWords) return;

        if (td.questionMode === "verbTraining") {
            const studentAnswers = _initVerbTrainingAnswers(td);
            newStudentData.questionAnswers![td.id] = {
                studentAnswers,
                exerciseType,
                gapMode: td.gapMode,
            };
        } else {
            const studentAnswers = _buildStudentAnswersForWords(td.selectedWords);

            if (td.questionMode === "fillInTheGap") {
                newStudentData.questionAnswers![td.id] = _initFillInTheGapAnswer(td, studentAnswers);
            } else if (td.questionMode === "jumbledWords") {
                newStudentData.questionAnswers![td.id] = _initJumbledWordsAnswer(td);
            } else if (td.questionMode === "verbinden") {
                newStudentData.questionAnswers![td.id] = _initVerbindenAnswer(td, studentAnswers);
            } else {
                newStudentData.questionAnswers![td.id] = {
                    studentAnswers,
                    exerciseType,
                };
            }
        }
    });

    const testHasOnlyOneExercise = teacherTest.content.length === 1;
    return { studentTestAnswer: newStudentData, testHasOnlyOneExercise };
};

export { buildStudentTestAnswer };
